import { Injectable, signal } from '@angular/core';

type TranslationValue = string | TranslationObject;

/** Language the operator picked from the selector, scoped to this browser tab. */
const LANGUAGE_OVERRIDE_KEY = 'languageOverride';

/** Pre-SCS-2225 preference that permanently overrode the Berghaus Connect language. */
const LEGACY_LANGUAGE_KEY = 'language';

interface TranslationObject {
    [key: string]: TranslationValue;
}

@Injectable({
    providedIn: 'root',
})
export class TranslationService {
    private readonly _currentLang = signal<string>('en');
    private readonly _translations = signal<TranslationObject>({});
    private readonly _supportedLanguages = ['en', 'de', 'nl'];

    readonly currentLang = this._currentLang.asReadonly();
    readonly supportedLanguages = this._supportedLanguages;

    readonly translationsLoaded = signal(false);
    readonly hasSessionOverride = signal(false);

    private static readonly _apiLanguageMap: Record<string, string> = {
        'En': 'en',
        'De': 'de',
        'Nl': 'nl',
    };

    constructor() {
        this.detectLanguage();
    }

    async initialize(): Promise<void> {
        await this.loadTranslations(this._currentLang());
        this.translationsLoaded.set(true);
    }

    private detectLanguage(): void {
        localStorage.removeItem(LEGACY_LANGUAGE_KEY);

        const overrideLang = sessionStorage.getItem(LANGUAGE_OVERRIDE_KEY);
        if (overrideLang && this._supportedLanguages.includes(overrideLang)) {
            this._currentLang.set(overrideLang);
            this.hasSessionOverride.set(true);
            return;
        }

        const browserLang = navigator.language.split('-')[0];
        if (this._supportedLanguages.includes(browserLang)) {
            this._currentLang.set(browserLang);
        }
    }

    /** Applies the user's Berghaus Connect language. Not remembered: it is re-read on every load. */
    async setLanguageFromApi(apiLanguageId: string): Promise<void> {
        const lang = TranslationService._apiLanguageMap[apiLanguageId];
        if (lang) {
            await this.applyLanguage(lang);
        }
    }

    /** Applies the operator's explicit choice, remembered until this tab is closed. */
    async setLanguage(lang: string): Promise<void> {
        if (!this._supportedLanguages.includes(lang)) {
            console.warn(`Language '${lang}' is not supported`);
            return;
        }

        sessionStorage.setItem(LANGUAGE_OVERRIDE_KEY, lang);
        this.hasSessionOverride.set(true);
        await this.applyLanguage(lang);
    }

    private async applyLanguage(lang: string): Promise<void> {
        this._currentLang.set(lang);
        await this.loadTranslations(lang);
    }

    private async loadTranslations(lang: string): Promise<void> {
        try {
            const response = await fetch(`/assets/locale/messages.${lang}.json`);
            if (!response.ok) {
                throw new Error(`Failed to load translations for ${lang}`);
            }
            const translations = await response.json();
            this._translations.set(translations);
        } catch (error) {
            console.error(`Failed to load translations for ${lang}:`, error);
            // Fall back to English if loading fails
            if (lang !== 'en') {
                await this.loadTranslations('en');
            }
        }
    }

    translate(key: string, params?: Record<string, string | number>): string {
        const keys = key.split('.');
        let value: TranslationValue = this._translations();

        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                return key; // Return key if translation not found
            }
        }

        if (typeof value !== 'string') {
            return key;
        }

        // Replace parameters like {{param}}
        if (params) {
            return Object.entries(params).reduce(
                (acc, [paramKey, paramValue]) =>
                    acc.replaceAll(new RegExp(`{{${paramKey}}}`, 'g'), String(paramValue)),
                value
            );
        }

        return value;
    }

    t(key: string, params?: Record<string, string | number>): string {
        return this.translate(key, params);
    }
}
